<template>
  <div class="list-room-renovation-record-details-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listRoomRenovationRecordDetails.page.title')" :subtitle="subtitleText">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('listRoomRenovationRecordDetails.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('listRoomRenovationRecordDetails.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="roomRenovationRecordDetailsInfo.roomRenovationRecordDetails" :loading="loading"
        :error="hasError" :error-message="errorMessage" :density="density" border
        :empty-description="$t('listRoomRenovationRecordDetails.page.empty')" @retry="refreshList">
        <el-table-column prop="roomName" :label="$t('listRoomRenovationRecordDetails.room')" align="center" min-width="120" />
        <el-table-column :label="$t('listRoomRenovationRecordDetails.media')" align="center" width="240">
          <template slot-scope="scope">
            <div v-if="scope.row.relTypeCd === '19000'" class="ds-media-preview">
              <el-image :src="scope.row.url" :preview-src-list="[scope.row.url]" fit="cover" class="ds-media-preview__img">
                <div slot="error" class="ds-media-preview__img">
                  <img src="/img/noPhoto.jpg" class="ds-media-preview__img">
                </div>
              </el-image>
              <i class="el-icon-zoom-in ds-media-preview__icon"></i>
            </div>
            <video v-else-if="scope.row.relTypeCd === '21000'" class="ds-media-preview__video" controls>
              <source :src="scope.row.url" type="video/mp4">
            </video>
            <span v-else class="ds-media-preview__empty">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('listRoomRenovationRecordDetails.createTime')" align="center"
          width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('listRoomRenovationRecordDetails.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <view-image ref="viewImage" />
  </div>
</template>

<script>
import { queryRoomRenovationRecordDetail } from '@/api/community/listRoomRenovationRecordDetailsApi'
import ViewImage from '@/components/community/viewImage'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListRoomRenovationRecordDetails',
  components: {
    ViewImage,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      roomRenovationRecordDetailsInfo: {
        roomRenovationRecordDetails: [],
        conditions: {
          recordId: '',
          roomName: '',
          state: '',
          roomId: '',
          communityId: '',
          page: 1,
          row: 10
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 副标题：房屋编号（来源于路由 query，不额外请求）
     */
    subtitleText() {
      const roomName = this.roomRenovationRecordDetailsInfo.conditions.roomName
      return roomName
        ? roomName + ' · ' + this.$t('listRoomRenovationRecordDetails.page.subtitle')
        : this.$t('listRoomRenovationRecordDetails.page.subtitle')
    }
  },
  created() {
    this.roomRenovationRecordDetailsInfo.conditions.communityId = getCommunityId()
    this.roomRenovationRecordDetailsInfo.conditions.recordId = this.$route.query.recordId
    this.roomRenovationRecordDetailsInfo.conditions.roomName = this.$route.query.roomName
    this.roomRenovationRecordDetailsInfo.conditions.state = this.$route.query.state
    this._listRoomRenovationRecordDetails()
  },
  methods: {
    async _listRoomRenovationRecordDetails() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.roomRenovationRecordDetailsInfo.conditions,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total } = await queryRoomRenovationRecordDetail(params)
        this.roomRenovationRecordDetailsInfo.roomRenovationRecordDetails = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取装修记录详情失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('listRoomRenovationRecordDetails.page.loadFailed')
        this.$message.error(this.$t('listRoomRenovationRecordDetails.fetchError'))
      } finally {
        this.loading = false
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this._listRoomRenovationRecordDetails()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listRoomRenovationRecordDetails()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listRoomRenovationRecordDetails()
    },
    _goBack() {
      this.$router.go(-1)
    },
    showImg(url) {
      this.$refs.viewImage.open(url)
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.list-room-renovation-record-details-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 媒体预览 */
.ds-media-preview {
  position: relative;
  display: inline-block;

  &__img {
    width: 72px;
    height: 72px;
    border-radius: var(--ds-radius-sm);
    object-fit: cover;
    border: 1px solid var(--ds-gray-200);
  }

  &__icon {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    font-size: 12px;
    color: #fff;
    background: rgba(0, 0, 0, 0.45);
    border-radius: var(--ds-radius-sm);
    pointer-events: none;
  }

  &__video {
    width: 200px;
    height: 112px;
    border-radius: var(--ds-radius-sm);
    background: #000;
  }

  &__empty {
    color: var(--ds-gray-400);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
